/**
 * All possible step keys used by the stepper.
 * @enum {string}
 * @readonly
 * @property {string} ESSENTIAL_INFO - Step for essential information.
 * @property {string} FAMILY_MEMBERS - Step for adding family members.
 * @property {string} EVENTS - Step for adding events.
 * @property {string} ADDITIONAL_FACTS - Step for additional facts.
 * @property {string} REVIEW_ALL - Step for reviewing all information.
 */
export const STEPS = {
  ESSENTIAL_INFO: 'essentialInfo',
  FAMILY_MEMBERS: 'familyMembers',
  EVENTS: 'events',
  ADDITIONAL_FACTS: 'additionalFacts',
  REVIEW_ALL: 'reviewAll',
}

/**
 * A step key string (one of STEPS values).
 * @typedef {('essentialInfo'|'familyMembers'|'events'|'additionalFacts'|'reviewAll')} StepKey
 */

/**
 * Record of boolean flags keyed by StepKey, used for reviewed/editMode maps.
 * @typedef {Record<StepKey, boolean>} StepFlags
 */

/**
 * Build an object of boolean flags for each step defined in STEPS.
 * Keeps reviewed/editMode maps in sync with STEPS automatically.
 *
 * @returns {StepFlags} object with a false boolean flag for each step
 */
const buildFlagsFromSteps = () =>
  Object.values(STEPS).reduce((acc, step) => {
    acc[step] = false
    return acc
  }, {})

/**
 * State shape used by the stepper reducer.
 * @typedef {Object} StepperState
 * @property {StepFlags} reviewed
 * @property {StepFlags} editMode
 * @property {StepKey} currentEditStep
 * @property {StepKey} previousEditStep
 */

/**
 * Initial reducer state.
 * - reviewed: map of which steps have been reviewed
 * - editMode: which steps are currently in edit mode
 * - currentEditStep: the active step key. If this step isn't in editMode, it will enter edit mode when the active step is saved
 * - previousEditStep: the last step that was in edit mode before the current one
 *
 * @type {StepperState}
 */
export const stepperInitialState = {
  reviewed: buildFlagsFromSteps(),
  editMode: { ...buildFlagsFromSteps(), [STEPS.ESSENTIAL_INFO]: true },
  currentEditStep: STEPS.ESSENTIAL_INFO,
  previousEditStep: null,
}

/**
 * Initial reducer state for edit view, which doesn't automatically advance through steps.
 * @type {StepperState}
 */
export const stepperEditViewInitialState = {
  reviewed: buildFlagsFromSteps(),
  editMode: buildFlagsFromSteps(),
  currentEditStep: null,
  previousEditStep: null,
}

/**
 * Action shapes accepted by the reducer.
 * - SET_REVIEWED: { type: 'SET_REVIEWED', step: StepKey, value: boolean }
 * - SET_EDIT_MODE: { type: 'SET_EDIT_MODE', step: StepKey, value: boolean }
 * - SET_CURRENT_STEP: { type: 'SET_CURRENT_STEP', step: StepKey }
 * - RESET: { type: 'RESET' }
 * - BATCH: { type: 'BATCH', actions: StepperAction[] }
 *
 * @typedef {Object} StepperAction
 * @property {string} StepperAction.type
 * @property {StepKey} [StepperAction.step]
 * @property {boolean} [StepperAction.value]
 * @property {StepperAction[]} [StepperAction.actions]
 */

/**
 * Reducer to manage step review and edit state.
 *
 * Pure function: given state and action returns next state.
 * Designed for use with React.useReducer to centralize stepper state.
 *
 * @param {StepperState} state
 * @param {StepperAction} action
 * @returns {StepperState}
 */
export const stepperReducer = (state, action) => {
  switch (action.type) {
    case 'SET_REVIEWED':
      return {
        ...state,
        reviewed: { ...state.reviewed, [action.step]: !!action.value },
      }
    case 'SET_EDIT_MODE': {
      // Default other cards to false, keeping only one card open at once
      const newEditMode = { ...buildFlagsFromSteps(), [action.step]: !!action.value }
      let newPreviousEditStep = state.currentEditStep
      if (!action.value) {
        // When leaving edit mode on a card, open the current edit step, which may be the card that was open previously.
        if (action.returnToPreviousStep && state.previousEditStep) {
          newEditMode[state.previousEditStep] = true
          newPreviousEditStep = null
        } else if (!action.returnToPreviousStep) {
          newEditMode[state.currentEditStep] = true
        }
      } else {
        newPreviousEditStep = Object.keys(state.editMode).find((key) => state.editMode[key]) || null
      }
      return {
        ...state,
        editMode: newEditMode,
        previousEditStep: newPreviousEditStep,
      }
    }
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentEditStep: action.step,
        editMode: { ...buildFlagsFromSteps(), [action.step]: true },
      }
    case 'RESET':
      return stepperInitialState
    case 'BATCH':
      if (!Array.isArray(action.actions)) return state

      return action.actions.reduce((s, a) => stepperReducer(s, a), state)
    default:
      return state
  }
}
